// src/components/SideMenu.jsx
import { Paper, Typography, Box, Tooltip } from '@mui/material';
import {
  TextFields,
  ShortText,
  List,
  CheckBox,
  RadioButtonChecked,
  DateRange,
  AttachFile,
  Edit,
  Person,
  Email,
  Phone,
  Numbers,
  AccessTime,
  TableChart,
  Calculate,
  Image as ImageIcon,
  InsertDriveFile as FileIcon,
  PictureAsPdf as PdfIcon,
  Description,
  Info,
  Draw,
  TableRows,
  Functions,
  LocationOn,
  // Yeni iconlar
  ToggleOn,
  Tune,
  Palette,
  Facebook,
  Twitter,
  Instagram,
  LinkedIn,
  YouTube,
  WhatsApp,
  Share,
  Speed,
  TrendingUp,
  Fingerprint,
  Brush,
  QrCode,
  AttachMoney,
  Star,
  LinearScale,
  Link,
  // Sürdürülebilirlik iconları
  Park,
  LocalFlorist,
  Recycling,
  EmojiEvents,
  Nature,
  Public,
} from '@mui/icons-material';
import PropTypes from 'prop-types';
import { useTranslation } from 'react-i18next';

const SideMenu = ({ onAddField = () => {}, currentPage = 1 }) => {
  const { t } = useTranslation();

  const FIELD_TYPES_LOCALIZED = [
    {
      id: 'text',
      label: t('short_text'),
      icon: ShortText,
      description: t('short_text_desc'),
    },
    {
      id: 'textarea',
      label: t('long_text'),
      icon: Description,
      description: t('long_text_desc'),
    },
    {
      id: 'staticText',
      label: t('paragraph'),
      icon: Info,
      description: t('paragraph_desc'),
    },
    {
      id: 'email',
      label: t('email'),
      icon: Email,
      description: t('email_desc'),
    },
    {
      id: 'phone',
      label: t('phone'),
      icon: Phone,
      description: t('phone_desc'),
    },
    {
      id: 'number',
      label: t('number'),
      icon: Numbers,
      description: t('number_desc'),
    },
    {
      id: 'select',
      label: t('dropdown'),
      icon: List,
      description: t('dropdown_desc'),
    },
    {
      id: 'checkbox',
      label: t('checkbox'),
      icon: CheckBox,
      description: t('checkbox_desc'),
    },
    {
      id: 'radio',
      label: t('radio'),
      icon: RadioButtonChecked,
      description: t('radio_desc'),
    },
    {
      id: 'date',
      label: t('date'),
      icon: DateRange,
      description: t('date_desc'),
    },
    {
      id: 'file',
      label: t('file_upload'),
      icon: FileIcon,
      description: t('file_upload_desc'),
      acceptedFileTypes: '.pdf,.doc,.docx,.xls,.xlsx',
      maxFileSize: 5,
      multiple: false
    },
    {
      id: 'image',
      label: t('image_upload'),
      icon: ImageIcon,
      description: t('image_upload_desc'),
      acceptedFileTypes: '.jpg,.jpeg,.png,.gif',
      maxFileSize: 2,
      multiple: true,
      maxFiles: 5
    },
    {
      id: 'pdfDocument',
      label: t('pdf_document'),
      icon: PdfIcon,
      description: t('pdf_document_desc'),
      acceptedFileTypes: '.pdf',
      maxFileSize: 20,
      multiple: false,
      maxFiles: 1,
      isReference: true
    },
    {
      id: 'name',
      label: t('name'),
      icon: Person,
      description: t('name_desc'),
    },
    {
      id: 'conditional',
      label: t('conditional_field'),
      icon: Edit,
      description: t('conditional_field_desc'),
    },
    {
      id: 'signature',
      label: t('signature'),
      icon: Draw,
      description: t('signature_desc'),
    },
    {
      id: 'time',
      label: t('time'),
      icon: AccessTime,
      description: t('time_desc'),
    },
    {
      id: 'table',
      label: t('table'),
      icon: TableRows,
      description: t('table_desc'),
    },
    {
      id: 'calculation',
      label: t('calculation'),
      icon: Functions,
      description: t('calculation_desc'),
    },
    {
      id: 'address',
      label: t('address'),
      icon: LocationOn,
      description: t('address_desc'),
    },
    // Etkileşimli Alanlar
    {
      id: 'toggle',
      label: 'Açma/Kapama',
      icon: ToggleOn,
      description: 'Açma/kapama düğmesi',
    },
    {
      id: 'range-slider',
      label: 'Aralık Kaydırıcı',
      icon: Tune,
      description: 'Min-max değer aralığı seçimi',
    },
    {
      id: 'color-picker',
      label: 'Renk Seçici',
      icon: Palette,
      description: 'Renk seçim alanı',
    },
    {
      id: 'slider',
      label: 'Kaydırıcı',
      icon: LinearScale,
      description: 'Tek değer kaydırıcı',
    },
    {
      id: 'rating',
      label: 'Değerlendirme',
      icon: Star,
      description: 'Yıldız değerlendirme',
    },
    {
      id: 'currency',
      label: 'Para Birimi',
      icon: AttachMoney,
      description: 'Para birimi girişi',
    },
    {
      id: 'url',
      label: 'Web Adresi',
      icon: Link,
      description: 'URL girişi',
    },
    {
      id: 'gauge',
      label: 'Gösterge',
      icon: Speed,
      description: 'Gösterge/Metre',
    },
    {
      id: 'progress',
      label: 'İlerleme Çubuğu',
      icon: TrendingUp,
      description: 'İlerleme göstergesi',
    },
    // Sosyal Medya Alanları
    {
      id: 'social-links',
      label: 'Sosyal Medya',
      icon: Share,
      description: 'Çoklu sosyal medya bağlantıları',
    },
    {
      id: 'facebook',
      label: 'Facebook',
      icon: Facebook,
      description: 'Facebook profil bağlantısı',
    },
    {
      id: 'twitter',
      label: 'Twitter/X',
      icon: Twitter,
      description: 'Twitter/X profil bağlantısı',
    },
    {
      id: 'instagram',
      label: 'Instagram',
      icon: Instagram,
      description: 'Instagram profil bağlantısı',
    },
    {
      id: 'linkedin',
      label: 'LinkedIn',
      icon: LinkedIn,
      description: 'LinkedIn profil bağlantısı',
    },
    {
      id: 'youtube',
      label: 'YouTube',
      icon: YouTube,
      description: 'YouTube kanal bağlantısı',
    },
    {
      id: 'whatsapp',
      label: 'WhatsApp',
      icon: WhatsApp,
      description: 'WhatsApp numarası',
    },
    // Görsel Alanlar
    {
      id: 'drawing',
      label: 'Çizim Alanı',
      icon: Brush,
      description: 'Serbest çizim alanı',
    },
    {
      id: 'qr-code',
      label: 'QR Kod',
      icon: QrCode,
      description: 'QR kod okuyucu',
    },
    // Sürdürülebilirlik Alanları
    {
      id: 'carbon-calculator',
      label: 'Karbon Ayak İzi',
      icon: Park,
      description: 'Çevresel etki hesaplayıcı',
    },
    {
      id: 'eco-impact-display',
      label: 'Çevre Etki Göstergesi',
      icon: LocalFlorist,
      description: 'Gerçek zamanlı çevresel etki',
    },
    {
      id: 'sustainability-score',
      label: 'Sürdürülebilirlik Skoru',
      icon: Recycling,
      description: 'Çevresel sürdürülebilirlik puanı',
    },
    {
      id: 'green-certificate',
      label: 'Yeşil Sertifika',
      icon: EmojiEvents,
      description: 'Dijital çevre sertifikası',
    },
  ];

  const FIELD_CATEGORIES_LOCALIZED = {
    temelAlanlar: {
      title: t('basic_fields'),
      fields: ['text', 'textarea', 'staticText', 'number', 'email', 'phone', 'url']
    },
    kisiselBilgiler: {
      title: t('personal_info'),
      fields: ['name', 'address']
    },
    secilebilirAlanlar: {
      title: t('selectable_fields'),
      fields: ['select', 'checkbox', 'radio']
    },
    etkileşimliAlanlar: {
      title: 'Etkileşimli Alanlar',
      fields: ['toggle', 'slider', 'range-slider', 'rating', 'color-picker', 'currency', 'gauge', 'progress']
    },
    tarihVeZaman: {
      title: t('date_and_time'),
      fields: ['date', 'time']
    },
    dosyaYukleme: {
      title: t('file_upload_category'),
      fields: ['file', 'image', 'pdfDocument']
    },
    sosyalMedya: {
      title: 'Sosyal Medya',
      fields: ['social-links', 'facebook', 'twitter', 'instagram', 'linkedin', 'youtube', 'whatsapp']
    },
    gorselAlanlar: {
      title: 'Görsel Alanlar', 
      fields: ['signature', 'drawing', 'qr-code']
    },
    surdurulebilirlik: {
      title: 'Sürdürülebilirlik & Çevre',
      fields: ['carbon-calculator', 'eco-impact-display', 'sustainability-score', 'green-certificate']
    },
    digerAlanlar: {
      title: t('other_fields'),
      fields: ['table', 'calculation']
    }
  };

  const handleDragStart = (e, fieldType) => {
    e.dataTransfer.setData('fieldType', fieldType);
    e.currentTarget.style.opacity = '0.5';
  };

  const handleDragEnd = (e) => {
    e.currentTarget.style.opacity = '1';
  };

  const handleFieldClick = (fieldType) => {
    onAddField(fieldType, currentPage);
  };

  return (
    <Box
      sx={{
        width: 280,
        p: 2,
        bgcolor: 'background.paper',
        borderRight: 1,
        borderColor: 'divider',
        height: '100%',
        overflowY: 'auto',
      }}
    >
      
      <Box sx={{ mt: 2 }}>
        {Object.entries(FIELD_CATEGORIES_LOCALIZED).map(([categoryKey, category]) => (
          <Box key={categoryKey} sx={{ mb: 3 }}>
            <Typography 
              variant="subtitle2" 
              sx={{ 
                mb: 1, 
                color: 'text.secondary',
                fontWeight: 'bold',
                pl: 1 
              }}
            >
              {category.title}
            </Typography>
            {category.fields.map(fieldId => {
              const field = FIELD_TYPES_LOCALIZED.find(f => f.id === fieldId);
              const Icon = field.icon;
              return (
                <Tooltip
                  key={field.id}
                  title={field.description}
                  placement="right"
                  arrow
                  componentsProps={{
                    tooltip: {
                      sx: {
                        maxWidth: 'none',
                        whiteSpace: 'pre-wrap',
                        fontSize: '0.875rem',
                        padding: '8px 12px',
                        lineHeight: 1.5,
                      }
                    },
                    popper: {
                      sx: {
                        '& .MuiTooltip-tooltip': {
                          backgroundColor: 'rgba(0, 0, 0, 0.87)',
                        }
                      }
                    }
                  }}
                >
                  <Paper
                    draggable
                    onDragStart={(e) => handleDragStart(e, field.id)}
                    onDragEnd={handleDragEnd}
                    onClick={() => handleFieldClick(field.id)}
                    sx={{
                      p: 2,
                      mb: 1,
                      cursor: 'pointer',
                      display: 'flex',
                      alignItems: 'center',
                      gap: 2,
                      transition: 'all 0.2s ease',
                      '&:hover': {
                        bgcolor: 'action.hover',
                        transform: 'translateX(4px)'
                      },
                      '&:active': {
                        bgcolor: 'action.selected',
                      }
                    }}
                  >
                    <Box
                      sx={{
                        width: 40,
                        height: 40,
                        borderRadius: '50%',
                        bgcolor:
                          field.id === 'file' ? '#e3f2fd'
                          : field.id === 'image' ? '#fff8e1'
                          : field.id === 'pdfDocument' ? '#e8f5e9'
                          : field.id === 'signature' ? '#f3e5f5'
                          : field.id === 'table' ? '#e0f2f1'
                          : field.id === 'calculation' ? '#fbe9e7'
                          : field.id === 'text' ? '#e1f5fe'
                          : field.id === 'textarea' ? '#fce4ec'
                          : field.id === 'staticText' ? '#ede7f6'
                          : field.id === 'email' ? '#e8f5e9'
                          : field.id === 'phone' ? '#fff3e0'
                          : field.id === 'number' ? '#f9fbe7'
                          : field.id === 'select' ? '#f3e5f5'
                          : field.id === 'checkbox' ? '#e0f7fa'
                          : field.id === 'radio' ? '#fbe9e7'
                          : field.id === 'date' ? '#e1f5fe'
                          : field.id === 'name' ? '#f1f8e9'
                          : field.id === 'conditional' ? '#f8bbd0'
                          : field.id === 'time' ? '#ffe0b2'
                          : field.id === 'address' ? '#e0f7fa'
                          : field.id === 'toggle' ? '#e8f5e9'
                          : field.id === 'slider' ? '#f3e5f5'
                          : field.id === 'range-slider' ? '#e3f2fd'
                          : field.id === 'rating' ? '#fff8e1'
                          : field.id === 'color-picker' ? '#fce4ec'
                          : field.id === 'currency' ? '#e8f5e9'
                          : field.id === 'url' ? '#e1f5fe'
                          : field.id === 'gauge' ? '#ede7f6'
                          : field.id === 'progress' ? '#f1f8e9'
                          : field.id === 'social-links' ? '#e3f2fd'
                          : field.id === 'facebook' ? '#e3f2fd'
                          : field.id === 'twitter' ? '#e1f5fe'
                          : field.id === 'instagram' ? '#fce4ec'
                          : field.id === 'linkedin' ? '#e3f2fd'
                          : field.id === 'youtube' ? '#ffebee'
                          : field.id === 'whatsapp' ? '#e8f5e9'
                          : field.id === 'drawing' ? '#f3e5f5'
                          : field.id === 'qr-code' ? '#ede7f6'
                          : '#f5f5f5',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        boxShadow: 1,
                        mr: 1
                      }}
                    >
                      <Icon sx={{ fontSize: 28, color:
                        field.id === 'file' ? '#1976d2'
                        : field.id === 'image' ? '#ffb300'
                        : field.id === 'pdfDocument' ? '#388e3c'
                        : field.id === 'signature' ? '#8e24aa'
                        : field.id === 'table' ? '#00897b'
                        : field.id === 'calculation' ? '#d84315'
                        : field.id === 'text' ? '#0288d1'
                        : field.id === 'textarea' ? '#c2185b'
                        : field.id === 'staticText' ? '#5e35b1'
                        : field.id === 'email' ? '#388e3c'
                        : field.id === 'phone' ? '#f57c00'
                        : field.id === 'number' ? '#afb42b'
                        : field.id === 'select' ? '#7b1fa2'
                        : field.id === 'checkbox' ? '#0097a7'
                        : field.id === 'radio' ? '#e64a19'
                        : field.id === 'date' ? '#039be5'
                        : field.id === 'name' ? '#689f38'
                        : field.id === 'conditional' ? '#ad1457'
                        : field.id === 'time' ? '#fbc02d'
                        : field.id === 'address' ? '#0097a7'
                        : field.id === 'toggle' ? '#4caf50'
                        : field.id === 'slider' ? '#9c27b0'
                        : field.id === 'range-slider' ? '#2196f3'
                        : field.id === 'rating' ? '#ff9800'
                        : field.id === 'color-picker' ? '#e91e63'
                        : field.id === 'currency' ? '#4caf50'
                        : field.id === 'url' ? '#2196f3'
                        : field.id === 'gauge' ? '#673ab7'
                        : field.id === 'progress' ? '#8bc34a'
                        : field.id === 'social-links' ? '#2196f3'
                        : field.id === 'facebook' ? '#1877f2'
                        : field.id === 'twitter' ? '#1da1f2'
                        : field.id === 'instagram' ? '#e4405f'
                        : field.id === 'linkedin' ? '#0077b5'
                        : field.id === 'youtube' ? '#ff0000'
                        : field.id === 'whatsapp' ? '#25d366'
                        : field.id === 'drawing' ? '#9c27b0'
                        : field.id === 'qr-code' ? '#673ab7'
                        : '#616161'
                      }} />
                    </Box>
                    <Typography>{field.label}</Typography>
                  </Paper>
                </Tooltip>
              );
            })}
          </Box>
        ))}
      </Box>
    </Box>
  );
};

SideMenu.propTypes = {
  onAddField: PropTypes.func,
  currentPage: PropTypes.number
};

export default SideMenu;